import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { SpaceProps } from 'styled-system';
import { space } from 'styled-system';

import ArticleSmallPrice, { RenderingStrategy } from 'common/components/Price/ArticleSmallPrice';
import { NameBlock } from 'common/components/ProductCard/components/NameBlock';
import { getProductCardDetails } from 'common/components/ProductCard/utils/get-product-card-details';
import ProductVariations from 'common/components/Variations/components/ProductVariationsV2';
import { ProductEvent } from 'common/enums/Tracking';
import { useMatchesViewport } from 'common/hooks/media';
import { textWithEllipsis } from 'common/theme/styles';
import { DrawerInteraction, Element } from 'common/types/tracking';
import { ENERGY_EFFICIENCY_HASH } from '../../../constants/energyClass';
import { useLocalization } from '../../../providers/LocaleProvider';
import type { ArticleLabelsList } from '../../../types/labels';
import type { Product } from '../../../types/product-card';
import { Flex } from '../../Flex';
import { CaptionRegularCR2, HeadlineH8, SubtitleST4 } from '../../Typography';
import Rating from './RatingV2';

interface ProductCardDetailsProps {
  article: Product;
  productIndex: number;
  visibleProductsCount: number;
  isMarketplace?: boolean;
  sortedLabels: ArticleLabelsList;
  minPrice: Nullable<number>;
  withProminentPricing?: boolean;
  isSmallVariant?: boolean;
}

interface GetRatingsMarginBottom {
  showVariants: boolean;
  showDelivery: boolean;
}

interface ProductCardContentProps extends ProductCardDetailsProps {
  showVariants: boolean;
  showDelivery: boolean;
  ratingMb: SpaceProps['mb'];
}

const prominentPricingOptions = {
  priceComponent: HeadlineH8,
  withMarketingRed: true,
  withLargerEnergyClass: true,
  withLargerOldPrice: true,
};

const articleSmallPriceStyles: SpaceProps = {
  mb: ['sp_4', null, null, 'sp_6'],
};
const ProductNameSmall = styled(SubtitleST4)`
  ${textWithEllipsis(2)};
`;

const getRatingsMarginBottom = ({ showVariants, showDelivery }: GetRatingsMarginBottom) =>
  showVariants || showDelivery ? ['sp_4', null, null, 'sp_6'] : null;

const StyledArticleSmallPrice = styled(ArticleSmallPrice)<SpaceProps>`
  ${space};
`;

// eslint-disable-next-line complexity
const ProductCardContent = ({
  article: {
    sku,
    url,
    name,
    prices: { regular, special, rrp, measurement },
    energyClass,
    ratings: { count, average },
    brand,
    variations,
    bestOffer,
  },
  isMarketplace,
  sortedLabels,
  showVariants,
  showDelivery,
  ratingMb,
  minPrice,
  withProminentPricing,
  isSmallVariant = false,
}: ProductCardContentProps) => {
  const { t } = useLocalization();
  const { matches } = useMatchesViewport(true, true, false);
  const isDesktop = matches.matchesDesktop || matches.matchesLargeDesktop;

  return (
    <Flex flexDirection="column" pt="sp_8">
      {regular && (
        <StyledArticleSmallPrice
          {...articleSmallPriceStyles}
          energyClass={energyClass}
          energyClassHref={`${url}${ENERGY_EFFICIENCY_HASH}`}
          specialPrice={special}
          rrpPrice={rrp}
          regularPrice={regular}
          measurement={measurement}
          renderingStrategy={RenderingStrategy.Horizontal}
          {...(withProminentPricing && prominentPricingOptions)}
          labels_v2={sortedLabels}
          isV2
        />
      )}
      <NameBlock
        Typography={isSmallVariant && isDesktop ? ProductNameSmall : undefined}
        isHome24={bestOffer?.isHome24}
        name={name}
        isMarketplace={isMarketplace}
        brandName={brand?.name}
        isV2
      />
      {!!average && (
        <Rating average={average} count={count} {...(ratingMb ? { mb: ratingMb } : {})} />
      )}
      {showVariants && variations && (
        <ProductVariations
          sku={sku}
          url={url}
          variations={variations}
          minPrice={minPrice}
          trackingContext={{
            eventName: ProductEvent.ViewClick,
            element: Element.Button,
            interaction: DrawerInteraction.Open,
          }}
        />
      )}
      {showDelivery && (
        <CaptionRegularCR2 color="sys.success.text.default">
          {t('h24_immediately_available')}
        </CaptionRegularCR2>
      )}
    </Flex>
  );
};

export const ProductCardDetailsV2 = (props: ProductCardDetailsProps) => {
  const { showVariants, showDelivery } = getProductCardDetails(props.article);
  const ratingMb = getRatingsMarginBottom({ showVariants, showDelivery });

  return (
    <ProductCardContent
      {...props}
      showVariants={showVariants}
      showDelivery={showDelivery}
      ratingMb={ratingMb}
    />
  );
};
